import { PhotoModel } from '../../types/protocols';
import type { AssessmentGroupKey } from '../../Helpers/assessmentGroups';

// Inspection category enum (English values with Swedish translations in EnumTranslations)
export enum InspectionCategory {
    MeterReadings = 'meter_readings',
    RoutineOperations = 'routine_operations',
    Service = 'service',
    /** Skickbedömning: the three system templates (see Helpers/assessmentGroups.ts). Completed only from iOS. */
    ConditionAssessment = 'condition_assessment'
}

// Regulatory requirement type enum (for inspection templates that are myndighetskrav)
export enum RegulatoryType {
    Fire = 'fire',
    Ventilation = 'ventilation',
    Elevator = 'elevator',
    DrinkingWater = 'drinking_water',
    RoofSafety = 'roof_safety',
    Other = 'other'
}

export interface BuildingCheckTemplate {
    id: string;
    name: string;
    category: InspectionCategory;
    description: string;
    /** Suggested component type (a standard `config/buildComponentTypes` key): pre-filters the component picker when checks are added; never a link, each check stores its own componentKey */
    componentSpecificType?: string;
    componentBaseTypeGroup?: AssessmentGroupKey; // Skickbedömning only: auto-links all live components in the group
    system?: boolean; // System template (the three Skickbedömning templates): not deletable, category and group locked
    regulatoryType?: RegulatoryType; // If set, template is a regulatory requirement (myndighetskrav)
    estimatedTime: number; // in minutes
    takesMeasurement?: {
        metric: string; // kg, liter, mm, etc.
        acceptableRange?: {
            min?: number;
            max?: number;
        };
    };
    instructionalAttachments?: { [key: string]: PhotoModel }; // PhotoModel objects for attachments
    interval: {
        value: number;
        unit: 'days' | 'weeks' | 'months' | 'years';
    };
    nextDueDateCalculation: 'scheduled' | 'actual'; // relative to scheduled or actual due date
    photoRequired: boolean;
    estimatedCost: number;
    advanceNotice: number; // days before due date to notify
    createdAt: string;
    updatedAt: string;
}

export interface BuildingCheckTemplateFormData {
    name: string;
    category: InspectionCategory;
    description: string;
    componentSpecificType?: string; // Suggested component type for new checks of the template
    componentBaseTypeGroup?: AssessmentGroupKey; // Selected assessment group (system templates only)
    system?: boolean; // Preserved from the template being edited; never set from the form
    isRegulatoryRequirement: boolean; // Checkbox state (UI only)
    regulatoryType?: RegulatoryType; // Dropdown selection
    estimatedTime: number;
    takesMeasurement: {
        enabled: boolean;
        metric: string;
        hasMinValue: boolean;
        hasMaxValue: boolean;
        minValue: number;
        maxValue: number;
    };
    instructionalAttachments: { [key: string]: PhotoModel };
    interval: {
        value: number;
        unit: 'days' | 'weeks' | 'months' | 'years';
    };
    nextDueDateCalculation: 'scheduled' | 'actual';
    photoRequired: boolean;
    estimatedCost: number;
    advanceNotice: number;
}

export const INTERVAL_UNITS = [
    { value: 'days', label: 'Dagar' },
    { value: 'weeks', label: 'Veckor' },
    { value: 'months', label: 'Månader' },
    { value: 'years', label: 'År' }
] as const;

export const NEXT_DUE_DATE_OPTIONS = [
    { value: 'scheduled', label: 'Relativt till planerat datum' },
    { value: 'actual', label: 'Relativt till faktiskt genomfört datum' }
] as const;

export const TEMPLATE_CATEGORIES = [
    { value: InspectionCategory.MeterReadings, label: 'Avläsningar' },
    { value: InspectionCategory.RoutineOperations, label: 'Löpande drift' },
    { value: InspectionCategory.Service, label: 'Service' },
    { value: InspectionCategory.ConditionAssessment, label: 'Skickbedömning' }
] as const;

export const REGULATORY_TYPE_OPTIONS = [
    { value: RegulatoryType.Fire, label: 'Brand' },
    { value: RegulatoryType.Ventilation, label: 'OVK' },
    { value: RegulatoryType.Elevator, label: 'Hiss' },
    { value: RegulatoryType.DrinkingWater, label: 'Dricksvatten' },
    { value: RegulatoryType.RoofSafety, label: 'Taksäkerhet' },
    { value: RegulatoryType.Other, label: 'Annat' }
] as const;

export type TemplateCategory = InspectionCategory;

